import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NewProjectModelFieldModule } from '@rappider/project-model-field';
import { EntityVisualizationService } from '../../../services/entity-visualization.service';
import { EntityVisualizationState } from '../../../utils/entity-interfaces';
import { AIChatGeneratorService } from '../../../services/ai-generator.service';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { CompiledComponentRendererComponent } from
  'libs/content-tree-renderer/src/lib/components/compiled-component-renderer/compiled-component-renderer.component';
import { ComponentDefinitionWithRelations, ProjectModelField, NewProjectModelField, ProjectModelWithRelations } from '@rappider/rappider-sdk';
import * as ProjectModelFieldActions from 'libs/project-model-field/src/lib/state/project-model-field.actions';
import { ProjectModelFieldUpdateData } from 'libs/project-model-field/src/lib/utils/project-model-field-update-data';
import { NewProjectModelFieldComponent } from 'libs/project-model-field/src/lib/components/new-project-model-field/new-project-model-field.component';
import { Actions, ofType } from '@ngrx/effects';
import { ComponentGeneratorService } from 'libs/content-editor/src/lib/services/component-generator.service';
import { getProjectModelsWithFields } from 'libs/shared/src/lib/state-selectors/get-models-with-fields-selector';
import { selectComponentDefinitionById } from 'libs/component-definition/src/lib/state/component-definition.selectors';

@Component({
  selector: 'rappider-ai-entity-visualization-canvas',
  standalone: true,
  imports: [
    CommonModule,
    NzButtonModule,
    NzToolTipModule,
    NzSpinModule,
    NzEmptyModule,
    NzIconModule,
    NzModalModule,
    NewProjectModelFieldModule,
    NzCollapseModule,
    CompiledComponentRendererComponent
  ],
  templateUrl: './ai-entity-visualization-canvas.component.html',
  styleUrls: ['./ai-entity-visualization-canvas.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiEntityVisualizationCanvasComponent implements OnInit, OnChanges, OnDestroy {

  @Input() projectModelId: string | undefined;
  @Input() componentDefinitionId: string | undefined;

  // Track initial projectModelId to determine if we started with an entity or component
  private initialProjectModelId: string | undefined;
  private componentDefinitionSubscription$: Subject<void> | null = null;

  // Component definition state
  componentDefinition: ComponentDefinitionWithRelations | undefined;

  @ViewChild('newProjectModelFieldComponent') newProjectModelFieldComponent?: NewProjectModelFieldComponent;

  // Consolidated state management
  private readonly destroy$ = new Subject<void>();
  private componentKeyUpdateTimeout: NodeJS.Timeout | null = null;
  private lastComponentDefinitionId: string | null = null;

  // UI State
  public readonly uiState = {
    isCreatingComponent: false,
    isInitialLoad: true,
    componentKey: 0
  };

  // Service state
  visualizationState: EntityVisualizationState = {
    isLoading: false,
    loadingMessage: '',
    selectedModel: null,
    availableModels: [],
    lastCreatedModel: null,
    shouldOpenSplitter: false
  };

  constructor(
    private readonly store: Store<unknown>,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly cd: ChangeDetectorRef,
    private readonly entityVisualizationService: EntityVisualizationService,
    private readonly aiChatGeneratorService: AIChatGeneratorService,
    private readonly componentGeneratorService: ComponentGeneratorService,
    private readonly notification: NzNotificationService,
    private readonly actions$: Actions
  ) {
    // Dependencies are injected via constructor parameters
  }

  ngOnInit(): void {
    // Determine initial mode: if componentDefinitionId exists, we're in component mode
    this.initialProjectModelId = this.componentDefinitionId ? undefined : this.projectModelId;
    this.initializeSubscriptions();
    this.loadComponentDefinition();
    this.handleComponentDefinitionId();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['componentDefinitionId']) {
      const change = changes['componentDefinitionId'];
      if (change.currentValue && !change.previousValue) {
        this.initialProjectModelId = undefined;
      }
      this.loadComponentDefinition();
    }

    if (!this.componentDefinitionId && this.initialProjectModelId === undefined && changes['projectModelId']) {
      const change = changes['projectModelId'];
      if (change.firstChange || change.previousValue === undefined) {
        this.initialProjectModelId = this.projectModelId;
      }
    }

    if (this.projectModelId) {
      this.entityVisualizationService.selectModel(this.projectModelId);
      this.subscribeToSaveActions();
    } else if (this.componentDefinitionId) {
      this.handleComponentDefinitionId();
    }

    this.cd.markForCheck();
  }


  /**
   * Handle component definition ID by finding associated project model
   */
  private handleComponentDefinitionId(): void {
    if (!this.componentDefinitionId) {
      return;
    }

    this.store.select(getProjectModelsWithFields)
      .pipe(takeUntil(this.destroy$))
      .subscribe((projectModels: ProjectModelWithRelations[] | undefined) => {
        if (!projectModels?.length) {
          return;
        }

        const projectModel = projectModels.find(pm => {
          const componentDefId = this.entityVisualizationService.getComponentDefinitionId(pm);
          return componentDefId === this.componentDefinitionId;
        });

        if (projectModel) {
          this.projectModelId = projectModel.id;
          this.entityVisualizationService.selectModel(projectModel.id);
          this.subscribeToSaveActions();
          this.cd.markForCheck();
        } else if (this.uiState.isInitialLoad) {
          this.uiState.isInitialLoad = false;
          this.cd.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    if (this.componentKeyUpdateTimeout) {
      clearTimeout(this.componentKeyUpdateTimeout);
      this.componentKeyUpdateTimeout = null;
    }
    if (this.componentDefinitionSubscription$) {
      this.componentDefinitionSubscription$.next();
      this.componentDefinitionSubscription$.complete();
      this.componentDefinitionSubscription$ = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize all subscriptions
   */
  private initializeSubscriptions(): void {
    this.subscribeToRouteParameters();
    this.subscribeToVisualizationState();
    this.subscribeToSplitterToggle();
    this.subscribeToSaveActions();
    this.subscribeToMetadataUpdates();
    this.subscribeToFieldUpdateSuccess();
    this.subscribeToFieldCreateSuccess();
  }

  /**
   * Subscribe to route parameter changes
   */
  private subscribeToRouteParameters(): void {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: ParamMap) => {
        const routeProjectModelId = params.get('projectModelId') ?? undefined;
        if (routeProjectModelId && !this.projectModelId && routeProjectModelId !== this.projectModelId) {
          this.projectModelId = routeProjectModelId;
          this.entityVisualizationService.selectModel(routeProjectModelId);
        } else if (this.projectModelId && !routeProjectModelId) {
          this.entityVisualizationService.selectModel(this.projectModelId);
        }
      });
  }

  /**
   * Load component definition from input or entity
   */
  private loadComponentDefinition(): void {
    const componentDefId = this.componentDefinitionId || this.getComponentDefinitionId();

    if (!componentDefId) {
      this.componentDefinition = undefined;
      this.cd.markForCheck();
      return;
    }

    // Skip if already loaded
    if (this.componentDefinition?.id === componentDefId) {
      return;
    }

    // Cleanup previous subscription
    if (this.componentDefinitionSubscription$) {
      this.componentDefinitionSubscription$.next();
      this.componentDefinitionSubscription$.complete();
      this.componentDefinitionSubscription$ = null;
    }

    // Load component definition from store
    this.componentDefinitionSubscription$ = new Subject<void>();
    this.store.select(selectComponentDefinitionById(componentDefId) as any)
      .pipe(takeUntil(this.componentDefinitionSubscription$), takeUntil(this.destroy$))
      .subscribe((componentDefinition: any) => {
        this.componentDefinition = componentDefinition;
        this.cd.markForCheck();
      });
  }

  /**
   * Subscribe to visualization state changes
   */
  private subscribeToVisualizationState(): void {
    this.entityVisualizationService.getVisualizationState$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.visualizationState = state;
        this.handleModelSelection(state);
        this.updateInitialLoadState(state);
        this.loadComponentDefinition();
        this.cd.markForCheck();
      });
  }

  /**
   * Handle model selection logic
   */
  private handleModelSelection(state: EntityVisualizationState): void {
    if (this.projectModelId && state.availableModels.length > 0) {
      const model = state.availableModels.find(m => m.id === this.projectModelId);
      if (model && model !== state.selectedModel) {
        this.entityVisualizationService.selectModel(this.projectModelId);
      }
    }
  }

  /**
   * Update initial load state
   */
  private updateInitialLoadState(state: EntityVisualizationState): void {
    if (this.componentDefinitionId && this.uiState.isInitialLoad) {
      this.uiState.isInitialLoad = false;
      this.cd.markForCheck();
    } else if (state.selectedModel && this.uiState.isInitialLoad) {
      this.uiState.isInitialLoad = false;
    }
  }

  /**
   * Subscribe to splitter toggle state changes
   */
  private subscribeToSplitterToggle(): void {
    this.entityVisualizationService.getShouldOpenSplitter$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(shouldOpen => {
        if (shouldOpen) {
          this.entityVisualizationService.clearSplitterFlag();
        }
      });
  }


  /**
   * Handle model selection by ID
   * @param modelId - Unique identifier of the model to select
   */
  onModelSelect(modelId: string) {
    this.entityVisualizationService.selectModel(modelId);
  }

  /**
   * Subscribe to save action results and update loading state
   */
  private subscribeToSaveActions(): void {
    // Subscribe to setDisplayComponent started events
    this.aiChatGeneratorService.subscribeToSetDisplayComponentStarted()
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ projectModelId }) => {
        if (this.projectModelId === projectModelId) {
          this.uiState.isCreatingComponent = true;
          this.cd.markForCheck();
        }
      });

    // Subscribe to setDisplayComponent success events to update component renderer
    this.aiChatGeneratorService.subscribeToSetDisplayComponentSuccess()
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ projectModelId }) => {
        if (this.projectModelId === projectModelId) {
          this.uiState.isCreatingComponent = false;
          this.lastComponentDefinitionId = null;
          this.updateComponentKey();
          this.cd.markForCheck();
        }
      });

    // Subscribe to setDisplayComponent error events
    this.aiChatGeneratorService.subscribeToSetDisplayComponentError()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.uiState.isCreatingComponent = false;
        this.cd.markForCheck();
      });

    // Subscribe to approveBranch started events
    this.aiChatGeneratorService.subscribeToApproveBranchStarted()
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ componentDefinitionId }) => {
        const currentComponentId = this.getComponentDefinitionId();
        if (currentComponentId === componentDefinitionId) {
          this.uiState.isCreatingComponent = true;
          this.cd.markForCheck();
        }
      });

    // Subscribe to approveBranch success events to update component renderer
    this.aiChatGeneratorService.subscribeToApproveBranchSuccess()
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ componentDefinitionId }) => {
        const currentComponentId = this.getComponentDefinitionId();
        if (currentComponentId === componentDefinitionId) {
          this.uiState.isCreatingComponent = false;
          this.updateComponentKey();
          this.cd.markForCheck();
        }
      });

    // Subscribe to approveBranch error events
    this.aiChatGeneratorService.subscribeToApproveBranchError()
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ componentDefinitionId }) => {
        const currentComponentId = this.getComponentDefinitionId();
        if (currentComponentId === componentDefinitionId) {
          this.uiState.isCreatingComponent = false;
          this.cd.markForCheck();
        }
      });
  }


  /**
   * Update component key with debounce to prevent excessive re-renders
   */
  private updateComponentKey(): void {
    const currentComponentId = this.getComponentDefinitionId();
    if (currentComponentId === this.lastComponentDefinitionId) {
      return;
    }

    if (this.componentKeyUpdateTimeout) {
      clearTimeout(this.componentKeyUpdateTimeout);
    }

    this.componentKeyUpdateTimeout = setTimeout(() => {
      this.lastComponentDefinitionId = currentComponentId;
      this.uiState.componentKey++;
      this.uiState.isInitialLoad = false;
      this.loadComponentDefinition();
      this.cd.detectChanges();
      this.componentKeyUpdateTimeout = null;
    }, 150);
  }

  /**
   * Check if component should be shown
   */
  shouldShowComponent(): boolean {
    const hasBundleUrl = this.getEntityComponentBundleUrl();
    const hasComponentDefId = this.getComponentDefinitionId();
    return !this.uiState.isCreatingComponent &&
           (!!hasBundleUrl || !!hasComponentDefId) &&
           !this.uiState.isInitialLoad;
  }

  /**
   * Check if empty state should be shown
   */
  shouldShowEmptyState(): boolean {
    return !this.uiState.isCreatingComponent &&
           !this.getEntityComponentBundleUrl() &&
           !this.uiState.isInitialLoad;
  }

  /**
   * Check if loading state should be shown
   */
  shouldShowLoadingState(): boolean {
    return this.uiState.isCreatingComponent || this.uiState.isInitialLoad;
  }


  /**
   * Subscribe to metadata updates for the selected model
   */
  private subscribeToMetadataUpdates(): void {
    this.entityVisualizationService.getVisualizationState$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        if (state.selectedModel && this.projectModelId === state.selectedModel.id) {
          this.updateComponentKey();
        }
      });
  }


  /**
   * Get entity component bundle URL for rendering
   * @returns Bundle URL string or null if not available
   */
  getEntityComponentBundleUrl(): string | null {
    if (this.componentDefinitionId) {
      return this.componentGeneratorService.getMainBundleUrl(this.componentDefinitionId);
    }
    return this.entityVisualizationService.getEntityComponentBundleUrl(this.visualizationState.selectedModel);
  }

  /**
   * Get component definition ID from selected project model metadata or componentDefinitionId
   * @returns Component definition ID string or null if not found
   */
  getComponentDefinitionId(): string | null {
    if (this.componentDefinitionId) {
      return this.componentDefinitionId;
    }
    return this.entityVisualizationService.getComponentDefinitionId(this.visualizationState.selectedModel);
  }

  /**
   * Check if component definition mode
   */
  get isComponentDefinitionMode(): boolean {
    return !!this.componentDefinitionId && this.initialProjectModelId === undefined;
  }

  /**
   * Get display name (component title or entity name)
   */
  get displayName(): string {
    if (this.isComponentDefinitionMode) {
      return this.componentDefinition?.title || 'Component';
    }
    return this.visualizationState.selectedModel?.name || 'Entity';
  }

  /**
   * Load branches for the current component
   */
  onProjectModelFieldDelete(projectModelField: ProjectModelField): void {
    const projectModelId = this.visualizationState.selectedModel?.id;
    this.store.dispatch(ProjectModelFieldActions.DeleteProjectModelField({
      payload: {
        projectModelFieldId: projectModelField.id,
        projectModelId: projectModelId,
        relationedTypeId: this.visualizationState.selectedModel?.relationedTypeId
      }
    }));
  }

  /**
   * Handle project model field update event
   * @param updateData - Project model field update data
   */
  onProjectModelFieldUpdate(updateData: ProjectModelFieldUpdateData): void {
    const projectModelId = this.visualizationState.selectedModel?.id;
    this.store.dispatch(ProjectModelFieldActions.UpdateProjectModelField({
      payload: {
        projectModelFieldId: updateData.projectModelFieldId,
        projectModelField: updateData.projectModelField,
        projectModelId: projectModelId
      }
    }));
  }

  /**
   * Handle project model field create event
   * @param newField - New project model field data
   */
  onProjectModelFieldCreate(newField: NewProjectModelField): void {
    const projectModelId = this.visualizationState.selectedModel?.id;
    if (!projectModelId) {
      this.notification.error('Error', 'Project model ID is required');
      return;
    }

    this.store.dispatch(ProjectModelFieldActions.CreateProjectModelField({
      payload: {
        projectModelField: {
          ...newField,
          projectModelId: projectModelId
        },
        relationedTypeId: this.visualizationState.selectedModel?.relationedTypeId
      }
    }));
  }

  /**
   * Subscribe to field update success events
   */
  private subscribeToFieldUpdateSuccess(): void {
    this.actions$.pipe(
      ofType(ProjectModelFieldActions.UpdateProjectModelFieldSuccessful),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (this.newProjectModelFieldComponent) {
        this.newProjectModelFieldComponent.setModeToRead();
      }
      this.cd.markForCheck();
    });
  }

  /**
   * Subscribe to field create success events
   */
  private subscribeToFieldCreateSuccess(): void {
    this.actions$.pipe(
      ofType(ProjectModelFieldActions.CreateProjectModelFieldSuccessful),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (this.newProjectModelFieldComponent) {
        this.newProjectModelFieldComponent.setModeToRead();
      }
      this.cd.markForCheck();
    });
  }
}

